import React from "react";
import {object as objectType} from "prop-types";
import cloneDeep from "lodash-es/cloneDeep";
import isEqual from "lodash-es/isEqual";
import intersection from "lodash-es/intersection";
import merge from "lodash-es/merge";
import Services, {subscriptionKeys} from "../services/Services";
import TelemetryManager from "../services/telemetry/Manager";
import RegistryManager from "../services/registry/Manager";
import {whoami as fetchWhoami} from "../utils/auth";
import Configuration, {
  subscriptionKeys as configurationSubscriptionKeys
} from "../Configuration";
import Subscriptions from "../Subscriptions";
import {bindAllClassMethods, lightOrDark, diffScopes} from "../utils/utils";
import {telemetryActions} from "../constants";
import {combineContexts, isUnentitled} from "./utils";

const storageKey = "ats-security-ribbon";

const transitiveKeys = [
  "applications",
  "ribbonConfiguration",
  "ribbonProps",
  "whoami",
  "whoamiError",
  "registryError",
  "hostConfig",
  "isExtension",
  "userSettings"
];

// Handles nested settings for sub-settings
const getSettingFromGroup = (settingGroup, key) => {
  if (!settingGroup) {
    return;
  }
  const keys = Object.keys(settingGroup);
  if (keys.includes(key)) {
    return settingGroup[key];
  }

  let setting = null;
  if (keys.includes("nestedGroup")) {
    const nestedGroup = settingGroup.nestedGroup;
    setting = getSettingFromGroup(nestedGroup, key);
  } else {
    keys.forEach(k => {
      const checkSetting = settingGroup[k],
        {settings: nestedSettings} = checkSetting;

      if (nestedSettings) {
        setting = getSettingFromGroup(nestedSettings, key);
      }
    });
  }

  return setting;
};

export const StateContext = React.createContext();

/**
 * @nodoc
 */
export class StateProvider extends React.Component {
  constructor(props) {
    super(props);

    const {value = {}} = props,
      {applications} = value;

    this.state = value;

    this.appContexts = {};

    this.subscriptions = {};

    this.notificationRenderers = {};

    this.initializeApplications(applications);

    bindAllClassMethods(this);
  }

  componentDidMount() {
    const {isExtension} = this.state;

    Subscriptions.subscribe(
      subscriptionKeys.credentialsSet,
      this._fetchUserInfo
    );

    Subscriptions.subscribe(
      subscriptionKeys.hostConfigFetched,
      this._handleHostConfig
    );

    Subscriptions.subscribe(
      subscriptionKeys.servicesError,
      this._handleServicesError
    );

    Subscriptions.subscribe(
      configurationSubscriptionKeys.referenceChanged,
      this.refresh
    );

    window.document &&
      window.document.addEventListener(
        "visibilitychange",
        this._handleVisibilityChange
      );

    if (isExtension) {
      window.addEventListener("focus", this._handleVisibilityChange);
    }

    this._mounted = true;

    // If we already have host config in Services, handle setup
    if (Services.apiHost) {
      this._handleHostConfig(true);
    }
  }

  componentWillUnmount() {
    this._mounted = false;

    if (this._cancelHostConfig) {
      this._cancelHostConfig();
    }

    if (this._cancelUserInfo) {
      this._cancelUserInfo();
    }

    if (this._cancelUserSettings) {
      this._cancelUserSettings();
    }

    if (this._cancelState) {
      this._cancelState();
    }

    if (this._cancelSaveState) {
      this._cancelSaveState();
    }

    Subscriptions.unsubscribe(
      subscriptionKeys.credentialsSet,
      this._fetchUserInfo
    );

    Subscriptions.unsubscribe(
      subscriptionKeys.hostConfigFetched,
      this._handleHostConfig
    );

    Subscriptions.unsubscribe(
      subscriptionKeys.servicesError,
      this._handleServicesError
    );

    Subscriptions.unsubscribe(
      configurationSubscriptionKeys.referenceChanged,
      this.refresh
    );

    window.document &&
      document.removeEventListener(
        "visibilitychange",
        this._handleVisibilityChange
      );

    window.removeEventListener &&
      window.removeEventListener("focus", this._handleVisibilityChange);

    const appIds = Object.keys(this.subscriptions);
    appIds.forEach(appId => {
      const subKeys = Object.keys(this.subscriptions[appId]);

      subKeys.forEach(subKey => {
        Subscriptions.unsubscribe(subKey, this.subscriptions[appId][subKeys]);
      });
    });
  }

  _handleHostConfig(data = {}) {
    const _this = this;

    if (!_this._mounted) {
      return;
    }

    let hostEnvironment;

    if (data) {
      if (data.error) {
        console.warn(
          "The Ribbon did not appear due to an error loading the config.json from the host server."
        );
        return;
      }
      const {IROH_UI_CONFIG = {}} = data,
        {environment} = IROH_UI_CONFIG || {};

      hostEnvironment = environment;
    }

    this.setState({hostConfig: !!data, hostEnvironment}, _this._fetchUserInfo);
  }

  _fetchUserInfo() {
    const {hostConfig} = this.state,
      {credentials, isDefaultAccount, apiHost} = Services;

    if (isDefaultAccount || !credentials || !apiHost || !hostConfig) {
      this.getStoredState();
      return;
    }

    const {promise, cancel} = fetchWhoami();

    promise
      .then(({data}) => {
        this.getStoredState(data);

        Services.orgEntitled = !isUnentitled(data);

        this._fetchUserSettings();

        return data;
      })
      .catch(e => {
        if (!this._mounted) {
          return;
        }

        console.error(e);

        this._lockDown(e);
      });

    this._cancelUserInfo = cancel;
  }

  _fetchUserSettings() {
    const missingRegistryScope = diffScopes(
      ["registry/user:read"],
      Services.claimScopes
    );

    if (missingRegistryScope) {
      console.warn(
        "SecureXRibbon: Missing user registry read scope, user settings will not be applied."
      );
      return;
    }

    const {promise, cancel} = RegistryManager.getUserSettings();

    promise.then(data => {
      this.setState({userSettings: data});
    });

    this._cancelUserSettings = cancel;
  }

  _handleServicesError({status, thrown, response}) {
    const {config} = response,
      {isExtension} = this.state,
      {hasRetryInterceptor} = Services;

    // The extension auth-retries failed queries with an
    // interceptor, so let that do the initial handling.
    // If it gets a 401 on a retry, bail.
    if (isExtension || hasRetryInterceptor) {
      if (status === 401 && config.__isRetry) {
        this._lockDown(thrown);
      }

      return;
    }

    // Otherwise, just bail out and lock down.
    if (status === 401) {
      this._lockDown(thrown);
    }
  }

  _handleVisibilityChange() {
    if (window.document && document.visibilityState === "visible") {
      this.getStoredState();
    }
  }

  _lockDown(whoamiError) {
    this.setState({whoami: null, whoamiError, application: "ribbon-home"});
  }

  initializeApplications(applications) {
    if (!applications) {
      this.combinedContexts = combineContexts(null);
      return;
    }

    applications.forEach(application => {
      this._initializeApplication(application);

      // Map notification renderers
      const {notifications = {}} = application,
        current = Object.keys(this.notificationRenderers),
        adding = Object.keys(notifications),
        duplicates = intersection(current, adding);

      if (duplicates.length) {
        console.warn(
          "Duplicate notification renderer key(s) defined:",
          duplicates.toString()
        );
      }

      Object.assign(this.notificationRenderers, notifications);
    });

    this.combinedContexts = combineContexts(this.appContexts);
  }

  _initializeApplication(application, deferContexts = true) {
    const {appId, components, subscriptions} = application,
      {context} = components;

    if (context) {
      this.appContexts[appId] = context;
    }

    this._initializeSubscriptions(appId, subscriptions);

    if (!deferContexts) {
      this.combinedContexts = combineContexts(this.appContexts);
    }
  }

  _initializeSubscriptions(appId, subscriptions) {
    const _this = this;

    if (!subscriptions) {
      return;
    }

    const subKeys = Object.keys(subscriptions);

    subKeys.forEach(subKey => {
      const func = subscriptions[subKey];

      const subfunc = (newValue, oldValue) => {
        func(
          {
            setApp: _this.setApp,
            setAppStateWithId: _this.setAppStateWithId,
            getAppSetting: _this.getAppSetting,
            appState: _this.state[appId] || {}
          },
          newValue,
          oldValue
        );
      };

      Subscriptions.subscribe(subKey, subfunc);

      if (!this.subscriptions[appId]) {
        this.subscriptions[appId] = {};
      }

      this.subscriptions[appId][subKey] = subfunc;
    });
  }

  getStoredState(userInfo) {
    if (this._cancelState) {
      this._cancelState();
    }

    const {credentials, isDefaultAccount} = Services,
      {whoamiError, registryError, hostConfig} = this.state;

    if (!hostConfig) {
      return;
    }

    if (!userInfo && whoamiError) {
      return;
    }

    if (registryError || !credentials || isDefaultAccount) {
      const localStorageState = this.getLocalStoredState();

      this.applyStoredState(localStorageState);

      return;
    }

    const {promise, cancel} = RegistryManager.getRibbonState();

    promise
      .then(({data}) => {
        // If all is good talking to the registry, clear local storage.
        const ls = window.localStorage;
        ls && ls.removeItem(storageKey);

        if (data.ribbonTheme) {
          if (this.state.ribbonTheme !== data.ribbonTheme) {
            const {theme: themeHooks = {}} = Configuration.hooks,
              {changed: themeChanged} = themeHooks;

            themeChanged && themeChanged(this.theme);
          }
        }

        this.applyStoredState(data, userInfo);
      })
      .catch(error => {
        const {name, status, response = {}} = error,
          {data = {}} = response,
          {error_description} = data;

        // Ignore cancels
        if (name === "RequestCancelled") {
          return;
        }

        if (status === 404) {
          // Not found, need to create
          if (userInfo) {
            const localState = this.getLocalStoredState();

            this.setState(
              {
                ...localState,
                whoami: userInfo,
                whoamiError: null,
                registryError:
                  error_description &&
                  !error_description.includes("missing registry key: ribbon") &&
                  error
              },
              this.save
            );
          } else {
            this.save();
          }
        } else if (status === 403) {
          // Permission error, fallback to local storage
          const localState = this.getLocalStoredState();

          this.applyStoredState(localState, userInfo, error);
        }
      });

    this._cancelState = cancel;
  }

  getLocalStoredState() {
    try {
      const ls = window.localStorage,
        storedState = ls && ls.getItem(storageKey);

      if (storedState) {
        return JSON.parse(storedState);
      }
    } catch (e) {
      console.warn("Failed to load state", e);
    }

    return {};
  }

  applyStoredState(data, userInfo, error) {
    const clone = cloneDeep(this.state);

    Object.assign(clone, data);

    if (error) {
      clone.registryError = error;
    }

    if (userInfo && !isEqual(userInfo, clone.whoami)) {
      clone.whoami = userInfo;
      clone.whoamiError = null;
    }

    TelemetryManager._ribbonActiveAppId = data.application;

    this.setState(clone);
  }

  setApp(appId, openRibbon = false, options = {}) {
    const {source} = options,
      currentApplication = this.state.application;

    if (appId === currentApplication) {
      if (openRibbon) {
        Subscriptions.publish("ats-open-ribbon");
      }
      return;
    }

    this.setState({application: appId}, () => {
      TelemetryManager._ribbonActiveAppId = appId;

      this.save();

      Subscriptions.publish(
        "ats-ribbon-app-changed",
        appId,
        currentApplication
      );

      TelemetryManager.publish({
        service: "ribbon",
        metadata: {
          action: telemetryActions.appChange,
          last: currentApplication,
          next: appId,
          source
        }
      });

      if (openRibbon) {
        Subscriptions.publish("ats-open-ribbon");
      }
    });
  }

  getAppState(appId) {
    return this.state[appId] || {};
  }

  setAppState(key, value) {
    const {application} = this.state,
      cloned = cloneDeep(this.state);

    if (!cloned[application]) {
      cloned[application] = {};
    }

    if (cloned[key] != null && isEqual(cloned[application][key], value)) {
      return;
    }

    cloned[application][key] = value;

    this.setState(cloned, this.save);
  }

  setAppStateWithId(appId, key, value) {
    const cloned = cloneDeep(this.state);

    if (!cloned[appId]) {
      cloned[appId] = {};
    }

    if (value) {
      cloned[appId][key] = value;
    } else {
      delete cloned[appId][key];
    }

    this.setState(cloned, this.save);
  }

  assignAppState(object, callback) {
    const _this = this,
      {application} = _this.state,
      cloned = cloneDeep(_this.state);

    if (!cloned[application]) {
      cloned[application] = {};
    }

    Object.assign(cloned[application], object);

    _this.setState(cloned, () => {
      callback && callback();

      _this.save();
    });
  }

  assignAppStateWithId(appId, object, callback) {
    const _this = this,
      cloned = cloneDeep(_this.state);

    if (!cloned[appId]) {
      cloned[appId] = {};
    }

    Object.assign(cloned[appId], object);

    _this.setState(cloned, () => {
      callback && callback();

      _this.save();
    });
  }

  mergeAppState(object) {
    const {application} = this.state,
      cloned = cloneDeep(this.state);

    if (!cloned[application]) {
      cloned[application] = {};
    }

    merge(cloned[application], object);

    this.setState(cloned, this.save);
  }

  mergeAppStateWithId(appId, object) {
    const cloned = cloneDeep(this.state);

    if (!cloned[appId]) {
      cloned[appId] = {};
    }

    merge(cloned[appId], object);

    this.setState(cloned, this.save);
  }

  getAppSetting(appId, key, options = {}) {
    const {value = {}} = this.props,
      {applications = []} = value,
      {[appId]: appState = {}} = this.state,
      appConfig = applications.find(
        ({appId: application}) => appId === application
      );

    if (!appConfig) {
      return null;
    }

    const appSettings = appConfig.settings,
      {ignoreDefault} = options,
      settingConfig = appSettings && getSettingFromGroup(appSettings, key);

    if (!settingConfig) {
      return null;
    }

    const {defaultValue} = settingConfig,
      {settings} = appState || {};

    if (!ignoreDefault) {
      if (!appState) {
        return defaultValue;
      }

      if (!settings || !settings.hasOwnProperty(key)) {
        return defaultValue;
      }
    }

    const setting = settings && settings[key];

    return setting;
  }

  setTheme(theme) {
    const {theme: themeHooks = {}} = Configuration.hooks,
      {changed: themeChanged} = themeHooks;

    this.setState({ribbonTheme: theme}, () => {
      themeChanged && themeChanged(this.theme);
      this.save();
    });
  }

  get theme() {
    const {ribbonTheme, isExtension, isMobile} = this.state;

    if (isMobile) {
      return;
    }

    const automaticTheme = this.getAppSetting("ribbon-home", "automaticTheme"),
      extensionTheme = this.getAppSetting("ribbon-home", "extensionTheme"),
      {productTheme, appRootElement} = Configuration.references,
      rootElement =
        typeof appRootElement === "string"
          ? window.document && window.document.getElementById(appRootElement)
          : appRootElement || (window.document && window.document.body);

    if (isExtension) {
      const {theme: themeHooks = {}} = Configuration.hooks,
        {changed: themeChanged} = themeHooks;
      themeChanged && themeChanged(extensionTheme);

      return extensionTheme;
    }

    if (ribbonTheme !== "automatic") {
      return ribbonTheme;
    }

    let pageTheme;

    // Check for theme classes on body
    const themeClasses = productTheme
      ? [productTheme]
      : [...rootElement.classList].filter(c => {
          return c.startsWith("theme");
        });

    if (themeClasses.length) {
      const defaultThemes = themeClasses.filter(c => {
          return (
            c.includes("default") || c.includes("white") || c.includes("light")
          );
        }),
        duskThemes = themeClasses.filter(c => {
          return c.includes("dusk");
        });

      if (defaultThemes.length) {
        pageTheme = "default";
      } else if (duskThemes.length) {
        pageTheme = "dusk";
      }
    } else {
      // Fall back to looking at the background color of <body>
      const bodyTheme = lightOrDark(
        window.getComputedStyle(rootElement).backgroundColor
      );

      pageTheme = bodyTheme === "light" ? "default" : "dusk";
    }

    if (automaticTheme === "invertTheme") {
      return pageTheme === "default" ? "theme--dusk" : "theme--default";
    } else {
      return `theme--${pageTheme}`;
    }
  }

  get productTheme() {
    const {theme} = this.state,
      {productTheme} = Configuration.references;

    return productTheme || theme;
  }

  save() {
    if (this._cancelSaveState) {
      this._cancelSaveState();
    }

    const {credentials} = Services,
      {registryError} = this.state,
      cloned = cloneDeep(this.state);

    Object.keys(cloned).forEach(k => {
      if (transitiveKeys.includes(k)) {
        delete cloned[k];
      }

      if (!cloned[k] || !Object.keys(cloned[k]).length) {
        delete cloned[k];
      }
    });

    if (registryError || !credentials) {
      try {
        const ls = window.localStorage,
          stringified = JSON.stringify(cloned);

        ls && ls.setItem(storageKey, stringified);
      } catch (e) {
        console.warn("Failed to save state", e);
      }

      // Bail out and don't use the registry
      return;
    }

    // If we do have access to the registry, use it
    const {promise, cancel} = RegistryManager.setRibbonState(cloned);

    promise
      .then(() => {
        if (this.state.registryError) {
          this.setState({registryError: null});
        }
      })
      .catch(e => {
        // Ignore cancels
        if (e.name === "RequestCancelled") {
          return;
        }

        console.warn("Error saving state to registry");
      });

    this._cancelSaveState = cancel;
  }

  reset() {
    const {value} = this.props,
      {application, applications = []} = this.state,
      cloned = cloneDeep(this.state);

    applications.forEach(({appId, onClearStorage}) => {
      const appState = this.state[appId];

      onClearStorage && onClearStorage({appState});
    });

    // Since we can't "replace state" entirely, seed the clone with the current state, drop everything that's not transitively loaded such as whoami, rekey the intitial value, and reset
    Object.keys(cloned).forEach(k => {
      if (!transitiveKeys.includes(k)) {
        cloned[k] = null;
      }
    });

    Object.assign(cloned, value);
    Object.assign(cloned, {application});

    this.setState(cloned, this.save);
  }

  get loading() {
    const {whoami, whoamiError} = this.state,
      {isDefaultAccount, credentials} = Services;

    if (isDefaultAccount || !credentials) {
      return false;
    }

    return !whoami && !whoamiError;
  }

  get error() {
    return this.state.whoamiError;
  }

  get canRender() {
    const {hostConfig, isExtension, isMobile} = this.state;

    if (isExtension || isMobile) {
      return true;
    }

    // No host config, just bail
    if (!hostConfig) {
      return false;
    }

    return true;
  }

  refresh() {
    this.forceUpdate();
  }

  render() {
    const {children} = this.props,
      {application, whoami, userSettings} = this.state,
      appState = this.state[application] || {},
      CombinedContext = this.combinedContexts;

    if (!this.canRender) {
      return null;
    }

    return (
      // Cannot use React.useMemo() in class components
      /* eslint-disable react/jsx-no-constructed-context-values */
      <StateContext.Provider
        value={{
          state: this.state,
          assignAppState: this.assignAppState, // Overwrite at root key
          assignAppStateWithId: this.assignAppStateWithId, // Overwrite at root key
          mergeAppState: this.mergeAppState, // Deep merge
          mergeAppStateWithId: this.mergeAppStateWithId, // Deep merge
          getAppState: this.getAppState,
          setAppState: this.setAppState,
          setAppStateWithId: this.setAppStateWithId,
          setApp: this.setApp,
          setTheme: this.setTheme,
          theme: this.theme,
          productTheme: this.productTheme,
          getAppSetting: this.getAppSetting,
          refresh: this.refresh,
          reset: this.reset,
          fetchState: this.getStoredState,
          notificationRenderers: this.notificationRenderers,
          loading: this.loading,
          error: this.error,
          whoami,
          userSettings,
          appState
        }}>
        <CombinedContext.Provider>{children}</CombinedContext.Provider>
      </StateContext.Provider>
    );
  }
}

export function withContext(Component) {
  return props => (
    <StateContext.Consumer>
      {context => {
        return <Component {...props} stateContext={context} />;
      }}
    </StateContext.Consumer>
  );
}

StateProvider.propTypes = {
  value: objectType,
  children: objectType,
  isExtension: objectType
};
